import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { AUTH_TOKEN, IS_MOBILE } from '../config';
import BottomSlot from './BottomSlot';
import { audioEngine } from '../services/audioEngine';
import useLiveAvatarSession, { fmtClock } from '../hooks/useLiveAvatarSession';

// LiveChatDock — the live Anam video call, staged INTO the room.
//
// The WebRTC stream lands in a hidden DOM <video> (Anam's SDK streams to a
// video element); the room's VideoPanel picks that element up from the store
// (liveVideoEl) and paints it onto the SAME card the recorded cameo uses —
// the avatar behind the booth window goes live, mic open, our brain talking.
//
// Plain DOM: must render OUTSIDE the Canvas (r3f can't host DOM nodes).
// Mic + billing both require a click, so everything is button-gated.

const VIDEO_EL_ID = 'world-live-video';

// NOT fixed any more — this chip lives in the bottom-centre stack (BottomSlot),
// which owns where the column sits and keeps the mobile lift above the HUD in
// ONE place. Pinning itself is how it ended up under the studio's session
// invite, which had pinned itself too.
const chip = {
  display: 'flex', alignItems: 'center', gap: 10,
  background: 'rgba(10,8,12,0.78)', border: '1px solid #ffb25e', borderRadius: 999,
  padding: '10px 16px', color: '#ffd27d', font: '14px system-ui, sans-serif',
  backdropFilter: 'blur(6px)', maxWidth: '86vw'
};
const btn = {
  background: '#ffb25e', color: '#1c1206', border: 'none', borderRadius: 999,
  padding: '7px 14px', font: '600 13px system-ui, sans-serif', cursor: 'pointer'
};
const ghostBtn = { ...btn, background: 'transparent', color: '#ffd27d', border: '1px solid #ffb25e' };

// Guests can't use live chat (or other studio functions). Instead of the old
// dev-token message, prompt them to join — a chip CTA that opens a modal linking
// to registration. Canonical public domain is always www.hypetunes.com.
const REGISTER_URL = 'https://www.hypetunes.com/register';
const modalWrap = {
  position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 24, background: 'radial-gradient(ellipse at center, rgba(6,3,0,0.5) 30%, rgba(6,3,0,0.8) 100%)',
};
const modalCard = {
  maxWidth: 340, width: '100%', background: 'rgba(18,11,7,0.97)', border: '1px solid rgba(255,178,94,0.4)',
  borderRadius: 16, padding: '22px 20px 18px', color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)', textAlign: 'center',
};

function GuestJoinGate({ name }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* One line; inherits the chip's mobile-lifted anchor so it clears the
          bottom HUD (joystick / now-playing / Music). */}
      <div style={{
        ...chip,
        whiteSpace: 'nowrap', maxWidth: '96vw', gap: 8, font: '12.5px system-ui, sans-serif',
      }}>
        <span style={{ opacity: 0.9 }}>Studio sessions are for members</span>
        <button style={{ ...btn, padding: '6px 12px', fontSize: 12.5 }} onClick={() => setOpen(true)}>Join HypeTunes</button>
      </div>
      {open && (
        <div style={modalWrap} onClick={() => setOpen(false)}>
          <div style={modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Join HypeTunes</div>
            <div style={{ fontSize: 14, opacity: 0.85, lineHeight: 1.5, marginBottom: 18 }}>
              Create a free account to chat live with {name} — and unlock the rest of the studio.
            </div>
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...btn, display: 'block', textDecoration: 'none', padding: '12px 0', fontSize: 15 }}
            >
              Join HypeTunes →
            </a>
            <button
              onClick={() => setOpen(false)}
              style={{ marginTop: 10, background: 'none', border: 'none', color: 'rgba(255,210,125,0.7)', font: '600 13px system-ui', cursor: 'pointer' }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function Dock({ roomId, liveChat }) {
  const setLiveVideoEl = useWorldStore((s) => s.setLiveVideoEl);
  const {
    phase, message, status, costPerMinute, minutes, secondsLeft, live, start, endCall, reset,
  } = useLiveAvatarSession({ avatarId: liveChat.avatarId, videoElementId: VIDEO_EL_ID });

  // While live: hand the stream element to the 3D panel and duck the room's
  // sound bed so the conversation owns the mix.
  useEffect(() => {
    if (!live) return undefined;
    setLiveVideoEl(document.getElementById(VIDEO_EL_ID));
    audioEngine.duck(true);   // voice/video chat → duck the bed, don't kill it
    return () => { setLiveVideoEl(null); audioEngine.duck(false); };
  }, [live, setLiveVideoEl]);

  // Leaving the room mid-call ends (and refunds) the session.
  const endRef = useRef(endCall);
  endRef.current = endCall;
  const liveRef = useRef(false);
  liveRef.current = live || phase === 'connecting';
  useEffect(() => () => { if (liveRef.current) endRef.current('ended'); }, []);

  const name = liveChat.name || 'the artist';
  const canOffer = !status || status.allowed !== false; // no status yet ≠ blocked; start() gives the real error

  return (
    <>
      {/* The stream target — offscreen but PLAYING (not display:none, which
          some browsers throttle). Audio comes straight from this element. */}
      <video
        id={VIDEO_EL_ID}
        playsInline
        autoPlay
        style={{ position: 'fixed', right: 0, bottom: 0, width: 2, height: 2, opacity: 0.01, pointerEvents: 'none' }}
      />

      <BottomSlot order={40}>
      {phase === 'confirm' && canOffer && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>
            {name} is in the booth
            {costPerMinute != null ? ` · ${costPerMinute} credits/min · ${minutes} min` : ''}
          </span>
          <button style={btn} onClick={start}>Go live</button>
        </div>
      )}
      {phase === 'confirm' && !canOffer && (
        <div style={chip}><span style={{ opacity: 0.8 }}>Live chat isn't available for {name} yet.</span></div>
      )}

      {phase === 'connecting' && (
        <div style={chip}><span>Calling {name}… {message}</span></div>
      )}

      {live && (
        <div style={{ ...chip, borderColor: '#ff5e78' }}>
          <span style={{ color: '#ff5e78', fontWeight: 700 }}>● LIVE</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtClock(secondsLeft)}</span>
          <span style={{ opacity: 0.75 }}>mic is open — just talk</span>
          <button style={ghostBtn} onClick={() => endCall('ended')}>End</button>
        </div>
      )}

      {(phase === 'ended' || phase === 'error') && (
        <div style={chip}>
          <span style={{ opacity: 0.9 }}>{message}</span>
          <button style={ghostBtn} onClick={reset}>{phase === 'error' ? 'Try again' : 'OK'}</button>
        </div>
      )}
      </BottomSlot>
    </>
  );
}

export default function LiveChatDock() {
  const zoom = useWorldStore((s) => s.zoom);
  const interior = useWorldStore((s) => s.interior);
  const liveChat = ROOMS[interior]?.liveChat;
  if (zoom !== ZOOM.INTERIOR || !liveChat) return null;

  // Guests don't get live chat — prompt them to join instead of the dev-token
  // message. (First slice of gating studio functions behind membership.)
  if (!AUTH_TOKEN) return <GuestJoinGate name={liveChat.name || 'the artist'} />;
  // key: fresh session state per room
  return <Dock key={interior} roomId={interior} liveChat={liveChat} />;
}
